import { useState, useEffect } from "react"
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import Sidebar from "./Components/Sidebar"
import Header from "./Components/Header"
import ClientConfiguration from "./Components/Pages/ClientConfig"
import Login from "./Components/Pages/Login"
import Dashboard from "./Components/Pages/Dashboard"
import Sitemaster from "./Components/Pages/SiteMaster"
import Employeemaster from "./Components/Pages/EmployeeMaster"
import Contractermaster from "./Components/Pages/ContracterMaster"
import TriggeredPage from "./Components/Pages/Triggered"
import Holidays from "./Components/Pages/Holidays"
import Attendance from "./Components/Pages/Attendance"
import AttendanceRecords from "./Components/Pages/AttendanceRecords"
import Repository from "./Components/Pages/Repository"
import Processed from "./Components/Pages/Processed"
import Revision from "./Components/Pages/Revision"
import Returns from "./Components/Pages/Returns"
import Logout from "./Components/Pages/Logout"
import ServiceCertificate from "./Components/Pages/ServiceCertificate"
import "./App.css"
import bgImage from "./Assets/Images/BG2.jpg"

// Enhanced session checker with better cross-tab communication
const SessionChecker = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    // Skip check on login page
    if (location.pathname === "/") {
      return
    }

    const isLoggedIn = sessionStorage.getItem("is_logged_in")
    const token = sessionStorage.getItem("token")
    const currentUser = sessionStorage.getItem("current_username")
    const currentSessionId = sessionStorage.getItem("session_id")

    console.log("SessionChecker - Session check:", { isLoggedIn, token: !!token, currentUser, currentSessionId })

    // If not logged in or no token, redirect to login
    if (!isLoggedIn || !token) {
      console.log("No valid session found, redirecting to login")
      navigate("/")
      return
    }

    // Listen for storage changes (cross-tab communication)
    const handleStorageChange = (e) => {
      console.log("SessionChecker - Storage change:", e.key, e.newValue)

      if (e.key === "active_user_sessions" && e.newValue) {
        const newSessions = JSON.parse(e.newValue)
        const currentUser = sessionStorage.getItem("current_username")
        const currentSessionId = sessionStorage.getItem("session_id")

        console.log("SessionChecker - Active sessions changed:", newSessions)
        console.log("SessionChecker - Current user:", currentUser, "Current session:", currentSessionId)

        if (currentUser && currentSessionId) {
          const userSession = newSessions[currentUser]

          // If there's a session for this user but it's not our session, we need to logout
          if (userSession && userSession.sessionId !== currentSessionId) {
            console.log("SessionChecker - Session conflict detected, logging out")
            sessionStorage.clear()
            navigate("/")
          }
        }
      }
    }

    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [location.pathname, navigate])

  return <>{children}</>
}

const AppLayout = ({ children, user, handleLogin, handleLogout }) => {
  const location = useLocation()
  const isLoginPage = location.pathname === "/"

  return (
    <div className="min-h-screen w-full bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${bgImage})` }}>
      {!isLoginPage && (
        <div className="flex h-screen">
          <Sidebar roleId={user?.roleId} />
          <div className="flex-1 flex flex-col">
            <Header user={user} onLogin={handleLogin} onLogout={handleLogout} />
            <main className="flex-1 p-6 overflow-auto">{children}</main>
          </div>
        </div>
      )}

      {isLoginPage && children}
    </div>
  )
}

function App() {
  // Get session data from sessionStorage
  const isLoggedIn = sessionStorage.getItem("is_logged_in")
  const token = sessionStorage.getItem("token")
  const username = sessionStorage.getItem("current_username")
  const roleId = sessionStorage.getItem("user_role_id")

  // Initialize user state from sessionStorage
  const [user, setUser] = useState(() => {
    if (isLoggedIn && token && username) {
      return {
        name: username,
        roleId: sessionStorage.getItem("user_role_id"),
        clientId: sessionStorage.getItem("client_id"),
        companyName: sessionStorage.getItem("company_name"),
      }
    }
    return null
  })

  const handleLogin = () => {
    const username = sessionStorage.getItem("current_username")
    const roleId = sessionStorage.getItem("user_role_id")
    const clientId = sessionStorage.getItem("client_id")
    const companyName = sessionStorage.getItem("company_name")
    const isLoggedIn = sessionStorage.getItem("is_logged_in")

    console.log("App - HandleLogin called:", { username, roleId, isLoggedIn })

    if (isLoggedIn && username) {
      setUser({ name: username, roleId, clientId, companyName })
    } else {
      setUser(null)
    }
  }

  const handleLogout = () => {
    // Clean up cross-tab communication
    const currentUsername = sessionStorage.getItem("current_username")
    if (currentUsername) {
      const existingSessions = JSON.parse(localStorage.getItem("active_user_sessions") || "{}")
      delete existingSessions[currentUsername]
      localStorage.setItem("active_user_sessions", JSON.stringify(existingSessions))
      console.log("App - Cleaned up session for", currentUsername)
    }

    // Clear this tab's session
    sessionStorage.clear()
    setUser(null)
  }

  // Clean up on tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentUsername = sessionStorage.getItem("current_username")
      if (currentUsername) {
        const existingSessions = JSON.parse(localStorage.getItem("active_user_sessions") || "{}")
        delete existingSessions[currentUsername]
        localStorage.setItem("active_user_sessions", JSON.stringify(existingSessions))
        console.log("App - Cleaned up session on tab close for", currentUsername)
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Router>
        <SessionChecker>
          <AppLayout user={user} handleLogin={handleLogin} handleLogout={handleLogout}>
            <Routes>
              <Route path="/" element={<Login onLogin={handleLogin} />} />

              {/* Protected Routes - Check sessionStorage instead of localStorage */}
              {isLoggedIn && token ? (
                <>
                  <Route path="/dashboard" element={<Dashboard />} />
                  {roleId === "1" && <Route path="/client-config" element={<ClientConfiguration />} />}
                  <Route path="/masters/site" element={<Sitemaster />} />
                  <Route path="/masters/employee" element={<Employeemaster />} />
                  <Route path="/masters/contractor" element={<Contractermaster />} />
                  {roleId === "20" && <Route path="/holidays" element={<Holidays />} />}
                  {roleId === "20" && <Route path="/attendance" element={<Attendance />} />}
                  {roleId === "20" && <Route path="/attendance-records" element={<AttendanceRecords />} />}
                  {roleId === "2" && <Route path="/triggered" element={<TriggeredPage />} />}
                  <Route path="/repository" element={<Repository />} />
                  {roleId === "2" && <Route path="/processed" element={<Processed />} />}
                   {roleId === "2" && <Route path="/revision" element={<Revision />} />}
                  {(roleId === "2" || roleId === "3") && <Route path="/returns" element={<Returns />} />}
                   {/* {roleId === "3" && <Route path="/service-certificate" element={<ServiceCertificate />} />} */}
                   {(roleId === "3"|| roleId === "2") &&  user?.companyName && ( user.companyName.trim().toLowerCase().includes("dusters") || 
                    user.companyName.trim().toLowerCase().includes("tickbox")) && 
                  <Route path="/service-certificate" element={<ServiceCertificate />} />}
                  <Route path="/logout" element={<Logout />} />
                  
                </>
              ) : (
                <>
                  <Route path="/dashboard" element={<Navigate to="/" />} />
                  <Route path="/client-config" element={<Navigate to="/" />} />
                  <Route path="/attendance-records" element={<Navigate to="/" />} />
                  <Route path="/attendance" element={<Navigate to="/" />} />
                  <Route path="/masters/site" element={<Navigate to="/" />} />
                  <Route path="/masters/employee" element={<Navigate to="/" />} />
                  <Route path="/masters/contractor" element={<Navigate to="/" />} />
                  <Route path="/triggered" element={<Navigate to="/" />} />
                  <Route path="/repository" element={<Navigate to="/" />} />
                  <Route path="/processed" element={<Navigate to="/" />} />
                  <Route path="/revision" element={<Navigate to="/" />} />
                  <Route path="/returns" element={<Navigate to="/" />} />
                  <Route path="/service-certificate" element={<Navigate to="/" />} />
                  <Route path="/logout" element={<Navigate to="/" />} />
                </>
              )}
            </Routes>
          </AppLayout>
        </SessionChecker>
      </Router>
    </LocalizationProvider>
  )
}

export default App
